import type { UploadOptions } from '@rpldy/uploady'
import type { ImageOptions } from '@tiptap/extension-image'
import Image from '@tiptap/extension-image'
import { mergeAttributes, ReactNodeViewRenderer } from '@tiptap/react'

import { getImageFullUrlInApplication } from '../../../utils'
import type { TokenRefreshManager } from '../../../utils/tokenRefreshManager'
import { ParseImagePlugin, UPLOAD_IMAGE_PLUGIN_KEY } from './plugin'
import { UploadImageReactRenderer } from './UploadImageReactRenderer'

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        customImg: {
            insertImg: (files: File[]) => ReturnType
            insertImageByUrl: (url: string) => ReturnType
        }
    }
}

export interface UploadImageOptions extends Partial<ImageOptions> {
    appId: string
    uploadOptions: UploadOptions
    tokenRefreshManager?: TokenRefreshManager
    /** 最大上传大小（字节），默认 10MB */
    maxSize?: number
}

export const UploadImage = Image.extend<UploadImageOptions>({
    draggable: true,

    addOptions() {
        return {
            ...this.parent?.(),
            appId: '',
            uploadOptions: {
                destination: {}
            },
            maxSize: 10 * 1024 * 1024 // 10MB default
        }
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'img',
            mergeAttributes(this.options.HTMLAttributes || {}, {
                ...HTMLAttributes,
                src: getImageFullUrlInApplication(this.options.appId, this.options.HTMLAttributes?.src),
                loading: 'lazy',
                decoding: 'async'
            })
        ]
    },

    addAttributes() {
        return {
            ...this.parent?.(),
            width: {
                default: null,
                parseHTML(element) {
                    return element.style.width
                },
                renderHTML(attributes) {
                    return {
                        width: attributes.width
                    }
                }
            },
            height: {
                default: null,
                parseHTML(element) {
                    return element.style.height
                },
                renderHTML(attributes) {
                    return {
                        height: attributes.height
                    }
                }
            }
        }
    },

    addCommands() {
        return {
            insertImg:
                files =>
                ({ view }) => {
                    const { tr } = view.state
                    tr.setMeta(UPLOAD_IMAGE_PLUGIN_KEY, files)
                    view.dispatch(tr)
                    return true
                },
            insertImageByUrl:
                url =>
                ({ commands }) =>
                    commands.insertContent({ type: this.name, attrs: { src: url } })
        }
    },

    addProseMirrorPlugins() {
        return this.editor.isEditable
            ? [
                  ParseImagePlugin({
                      appId: this.options.appId,
                      uploadOptions: this.options.uploadOptions,
                      tokenRefreshManager: this.options.tokenRefreshManager,
                      maxSize: this.options.maxSize
                  })
              ]
            : []
    },

    addNodeView(...args) {
        return ReactNodeViewRenderer(UploadImageReactRenderer)
    }
})
